/**
 * IN SHORT: This file handles Encryption (Confidentiality). 
 * It locks your data so only the recipient can read it using their Private Key.
 *
 * JWE (JSON Web Encryption) operations — RSA-OAEP-256 + A256GCM.
 *
 * SECURITY NOTES:
 * 1. Algorithm pinning: we set both `alg` (key encryption) and `enc` (content
 *    encryption) in the protected header. The decryption side checks them
 *    before delegating to the jose library.
 * 2. `cty: 'JWT'` is set when wrapping a JWS (nested JWT per RFC 7519 §6.2).
 *    This signals to the receiver that the plaintext is itself a JWT.
 * 3. A256GCM provides authenticated encryption — the AAD (protected header)
 *    is integrity-protected. An attacker cannot swap the `alg` or `enc`
 *    headers without causing authentication tag verification to fail.
 */

import { CompactEncrypt, compactDecrypt } from 'jose';
import type { CompactDecryptResult, CompactJWEHeaderParameters } from 'jose';
import { JwtSdkError, JwtSdkErrorCode, AlgorithmMismatchError } from '../errors/index.js';
import { KEY_WRAP_ALG, CONTENT_ENC_ALG } from '../types/index.js';
import { encodeUtf8, decodeUtf8, peekProtectedHeader } from '../utils/encoding.js';

export interface EncryptOptions {
  /** Plaintext string to encrypt (typically a compact JWS). */
  plaintext: string;
  /** Recipient's RSA public key (RSA-OAEP algorithm). */
  recipientPublicKey: CryptoKey;
  /** Key ID of the recipient's key — stored in the JWE header. */
  recipientKeyId?: string;
  /** Set to true when plaintext is itself a JWT (nested JWT). Adds `cty: 'JWT'`. */
  isNestedJwt?: boolean;
}

export interface DecryptOptions {
  /** Compact JWE string. */
  token: string;
  /** Recipient private key (RSA-OAEP algorithm). */
  privateKey: CryptoKey;
}

export interface DecryptResult {
  plaintext: string;
  protectedHeader: {
    alg: string;
    enc: string;
    cty?: string;
    kid?: string;
    [k: string]: unknown;
  };
}

/**
 * Encrypt plaintext using RSA-OAEP-256 + A256GCM.
 */
export async function encryptJwe(opts: EncryptOptions): Promise<string> {
  const plaintextBytes = encodeUtf8(opts.plaintext);

  const header: CompactJWEHeaderParameters = {
    alg: KEY_WRAP_ALG,
    enc: CONTENT_ENC_ALG,
  };
  if (opts.isNestedJwt) header['cty'] = 'JWT';
  if (opts.recipientKeyId) header['kid'] = opts.recipientKeyId;

  try {
    return await new CompactEncrypt(plaintextBytes)
      .setProtectedHeader(header)
      .encrypt(opts.recipientPublicKey);
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.ENCRYPTION_FAILED, 'JWE encryption failed', err);
  }
}

/**
 * Decrypt a compact JWE token and enforce algorithm pinning.
 */
export async function decryptJwe(opts: DecryptOptions): Promise<DecryptResult> {
  // Peek at the protected header before decryption to enforce algorithm pinning.
  let headerAlg: string | undefined;
  let headerEnc: string | undefined;
  try {
    const raw = peekProtectedHeader(opts.token) as { alg?: string; enc?: string };
    headerAlg = raw.alg;
    headerEnc = raw.enc;
  } catch {
    throw new JwtSdkError(JwtSdkErrorCode.INVALID_TOKEN, 'Malformed JWE token');
  }

  if (headerAlg !== KEY_WRAP_ALG) {
    throw new AlgorithmMismatchError(KEY_WRAP_ALG, headerAlg ?? 'undefined');
  }
  if (headerEnc !== CONTENT_ENC_ALG) {
    throw new AlgorithmMismatchError(CONTENT_ENC_ALG, headerEnc ?? 'undefined');
  }

  let result: CompactDecryptResult;
  try {
    result = await compactDecrypt(opts.token, opts.privateKey, {
      keyManagementAlgorithms: [KEY_WRAP_ALG],
      contentEncryptionAlgorithms: [CONTENT_ENC_ALG],
      maxDecompressedLength: Infinity,
    });
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.DECRYPTION_FAILED, 'JWE decryption failed', err);
  }

  const plaintext = decodeUtf8(result.plaintext);
  const ph = result.protectedHeader as DecryptResult['protectedHeader'];

  return { plaintext, protectedHeader: ph };
}
